import { convertOneFullNameToAbbreviation, type regionCodes } from '@avalara/bat-apps-shared/constants';
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import countries from '../lib/countries';
import { signupFormFields } from '../reducers/helpers/forms-constants';

import type { NewAccountForm, NewAccountFormField } from '@avalara/types/index';
import type { BuydotBillingContact } from '@avalara/types/index';
import type { SharedSignupState } from '@avalara/types/reducer-states';

const initialState: SharedSignupState = {
  /* fieldsClearedForValidation field will only consist of input fields
   * that the user has indicated are complete. An input is in this state
   * when onBlur event is fired (i.e. a user has clicked on the field,
   * enter information, and clicked away)
   */
  fieldsClearedForValidation: {},
  signupForm: signupFormFields,
  disableForm: false,
  emailIsVerified: true,
  isPrepopulated: false,
  isEditingPrepopulated: false,
  additionalRequiredFields: [],
  useEcmCompanyLength: false,
};

type FieldValuePayload = {
  field: NewAccountFormField;
  value: string;
};

type FillSharedFormPayload = {
  billingContact: BuydotBillingContact;
  companyName: string;
};

const store = createSlice({
  name: 'sharedSignup',
  initialState,
  reducers: {
    clearFieldForValidation: (state, action: PayloadAction<NewAccountFormField>) => {
      state.fieldsClearedForValidation[action.payload] = true;
    },
    fillSharedForm: (state, action: PayloadAction<FillSharedFormPayload>) => {
      const { billingContact, companyName } = action.payload;
      state.signupForm = {
        ...state.signupForm,
        email: billingContact.email ?? '',
        street1: billingContact.street || '',
        city: billingContact.city || '',
        firstName: billingContact.firstName ?? '',
        lastName: billingContact.lastName ?? '',
        country: countries.getAlpha2Code(billingContact.country || '', 'en') ?? 'US',
        state: convertOneFullNameToAbbreviation((billingContact.state as keyof typeof regionCodes) ?? ''),
        postalCode: billingContact.postalCode || '',
        companyName: companyName || 'DEFAULT',
        phone: billingContact.phone ?? 'DEFAULT',
      };
      state.disableForm = true;
    },
    receivePreFilledSignupForm: (state, action: PayloadAction<NewAccountForm>) => {
      const region = action.payload.state ?? state.signupForm.state ?? '';
      state.signupForm = {
        ...state.signupForm,
        ...action.payload,
        state: region.toUpperCase(),
      };
    },
    setAdditionalRequiredFields: (state, action: PayloadAction<Array<string>>) => {
      state.additionalRequiredFields = action.payload;
    },
    setUseEcmCompanyLength: (state, action: PayloadAction<boolean>) => {
      state.useEcmCompanyLength = action.payload;
    },
    setIsPrepopulated: (state, action: PayloadAction<boolean>) => {
      state.isPrepopulated = action.payload;
      state.isEditingPrepopulated = !action.payload;
    },
    setReadOnlyField: (state, action: PayloadAction<FieldValuePayload>) => {
      const { field, value } = action.payload;
      state.readOnlyFields = [...(state.readOnlyFields || []), field];
      state.signupForm = { ...state.signupForm, [field]: value };
    },
    signupFormChanged: (state, action: PayloadAction<FieldValuePayload>) => {
      const { field, value } = action.payload;
      const newSignupForm =
        field !== 'country'
          ? { ...state.signupForm, [field]: value }
          : {
              ...state.signupForm,
              country: value,
              city: '',
              state: '',
              postalCode: '',
            };
      state.signupForm = {
        ...newSignupForm,
        state: newSignupForm.state == null ? null : newSignupForm.state.toUpperCase(),
      };
      state.emailIsVerified = field === 'email' ? true : state.emailIsVerified;
    },
    toggleEmailVerification: (state, action: PayloadAction<boolean>) => {
      state.emailIsVerified = action.payload;
    },
  },
});

export const {
  clearFieldForValidation,
  fillSharedForm,
  receivePreFilledSignupForm,
  setAdditionalRequiredFields,
  setUseEcmCompanyLength,
  setIsPrepopulated,
  setReadOnlyField,
  signupFormChanged,
  toggleEmailVerification,
} = store.actions;

export default store;
